/**
 * Dronebilder: bildeindeksen fra API-et (`GET /api/projects/<id>/images`, laget av `powerline-lidar bildeindeks`).
 *
 * Rene funksjoner (parsing, URL-er, formatering, navigasjon) – testes i tests/unit/droneImages.test.ts.
 * Koordinater er prosjektets CRS (EPSG:25832) og høyder NN2000 når indeksen sier det (`z_reference`).
 */

import { trimBase } from '../config';

export type ZReference = 'NN2000' | 'ELLIPSOIDAL' | 'UNKNOWN';
export type ImageVariant = 'thumb' | 'preview' | 'original';

export interface DroneImage {
  id: string;
  /** plass i flyrekkefølgen (sortert på tid) */
  seq: number;
  fileName: string;
  fileSize: number | null;
  mission: string;
  /** ISO 8601 UTC */
  t: string | null;
  tUnix: number | null;
  x: number;
  y: number;
  z: number | null;
  zRef: ZReference;
  zEllipsoid: number | null;
  /** bakkehøyde under dronen (NN2000): terrengmodellen (DTM), ellers laseravstandsmåler; null = ukjent */
  groundZ: number | null;
  groundSource: 'DTM' | 'LRF' | null;
  relAlt: number | null;
  lrfDistance: number | null;
  /** kameraretning, grader fra geografisk nord med klokka */
  yaw: number | null;
  /** samme retning mot rutenettets nord (prosjektets CRS) – brukes til tegning og dekning */
  yawGrid: number | null;
  pitch: number | null;
  roll: number | null;
  flightYaw: number | null;
  camera: string | null;
  droneModel: string | null;
  hfov: number | null;
  vfov: number | null;
  width: number | null;
  height: number | null;
  rtkFlag: number | null;
  hasOriginal: boolean;
  hasDerivatives: boolean;
}

export interface MissionInfo { id: string; images: number; tStart: string | null; tEnd: string | null }

export interface ImageIndex {
  name: string;
  count: number;
  horizontalCrs: string | null;
  verticalCrs: string | null;
  heightReference: string;
  missions: MissionInfo[];
  /** sortert i flyrekkefølge (tid) */
  images: DroneImage[];
  byId: Map<string, DroneImage>;
  endpoints: Record<ImageVariant, string>;
}

/** Sammendraget i prosjekt-JSON (`image_index`) – antall uten å hente hele indeksen. */
export interface ImageSummary { available: boolean; count: number; url: string | null; heightReference?: string; tStart?: string | null; tEnd?: string | null }

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const SAFE_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;

export function imageSummary(raw: Record<string, unknown>): ImageSummary {
  const s = raw.image_index as Record<string, unknown> | undefined;
  if (!s || typeof s !== 'object') return { available: false, count: 0, url: null };
  const count = num(s.count) ?? 0;
  return { available: s.available === true && count > 0 && !!str(s.url), count, url: str(s.url),
    heightReference: str(s.height_reference) ?? undefined, tStart: str(s.t_start), tEnd: str(s.t_end) };
}

export function parseImage(e: unknown, fallbackSeq: number): DroneImage | null {
  if (!e || typeof e !== 'object') return null;
  const o = e as Record<string, unknown>;
  const id = str(o.id), x = num(o.x), y = num(o.y);
  if (!id || !SAFE_ID.test(id) || id.includes('..') || x === null || y === null) return null;
  const zr = o.z_reference;
  return {
    id, seq: num(o.seq) ?? fallbackSeq, fileName: str(o.file_name) ?? id, fileSize: num(o.file_size), mission: str(o.mission) ?? '',
    t: str(o.t), tUnix: num(o.t_unix), x, y, z: num(o.z), zRef: zr === 'NN2000' || zr === 'ELLIPSOIDAL' ? zr : 'UNKNOWN',
    zEllipsoid: num(o.z_ellipsoid), groundZ: num(o.ground_z_nn2000) ?? num(o.ground_z),
    groundSource: o.ground_source === 'DTM' || o.ground_source === 'LRF' ? o.ground_source : num(o.ground_z) !== null ? 'LRF' : null, relAlt: num(o.rel_alt), lrfDistance: num(o.lrf_distance),
    yaw: num(o.gimbal_yaw) ?? num(o.flight_yaw), yawGrid: num(o.yaw_grid) ?? num(o.gimbal_yaw), pitch: num(o.gimbal_pitch), roll: num(o.gimbal_roll),
    flightYaw: num(o.flight_yaw), camera: str(o.camera), droneModel: str(o.drone_model), hfov: num(o.hfov_deg), vfov: num(o.vfov_deg),
    width: num(o.width), height: num(o.height), rtkFlag: num(o.rtk_flag), hasOriginal: o.has_original !== false, hasDerivatives: o.has_derivatives !== false,
  };
}

/** Validerer og sorterer indeksen. Ugyldige oppføringer (ID, koordinater) hoppes over; dubletter av ID også. */
export function parseImageIndex(json: unknown, projectId: string): ImageIndex {
  if (!json || typeof json !== 'object') throw new Error('Bildeindeksen er ikke et JSON-objekt');
  const o = json as Record<string, unknown>;
  if (o.ok === false) throw new Error(`API svarte ok=false: ${String(o.error ?? 'ukjent feil')}`);
  if (!Array.isArray(o.images)) throw new Error('Bildeindeksen mangler images-liste');
  const seen = new Set<string>();
  const images: DroneImage[] = [];
  o.images.forEach((e, i) => { const img = parseImage(e, i); if (img && !seen.has(img.id)) { seen.add(img.id); images.push(img); } });
  images.sort((a, b) => (a.tUnix ?? Infinity) - (b.tUnix ?? Infinity) || a.seq - b.seq || (a.id < b.id ? -1 : 1));
  const ep = (o.endpoints ?? {}) as Record<string, unknown>;
  const base = `/api/projects/${encodeURIComponent(projectId)}/images/{id}/`;
  const endpoint = (v: ImageVariant) => (typeof ep[v] === 'string' && (ep[v] as string).includes('{id}') ? ep[v] as string : base + v);
  const missions = (Array.isArray(o.missions) ? o.missions : []).flatMap((m): MissionInfo[] => {
    const r = (m ?? {}) as Record<string, unknown>;
    return str(r.id) ? [{ id: r.id as string, images: num(r.images) ?? 0, tStart: str(r.t_start), tEnd: str(r.t_end) }] : [];
  });
  return {
    name: str(o.name) ?? projectId, count: images.length, horizontalCrs: str(o.horizontal_crs), verticalCrs: str(o.vertical_crs),
    heightReference: str(o.height_reference) ?? 'ukjent', missions, images, byId: new Map(images.map((i) => [i.id, i]) as [string, DroneImage][]),
    endpoints: { thumb: endpoint('thumb'), preview: endpoint('preview'), original: endpoint('original') },
  };
}

/** Absolutt URL for en variant: endepunktmal fra indeksen + API-basen. */
export function imageUrl(index: Pick<ImageIndex, 'endpoints'>, apiBase: string, id: string, variant: ImageVariant): string {
  const path = index.endpoints[variant].replace('{id}', encodeURIComponent(id));
  return /^https?:\/\//i.test(path) ? path : trimBase(apiBase) + (path.startsWith('/') ? path : '/' + path);
}

/** «EPSG:25832» og «EPSG:25832+5941» → «25832»; null når ukjent. */
export const epsgCode = (crs: string | null | undefined): string | null => (crs ? /(\d{4,5})/.exec(crs)?.[1] ?? null : null);

/** Bildene kan tegnes i prosjektet når horisontal CRS er lik (eller prosjektets CRS er ukjent). */
export function crsCompatible(index: Pick<ImageIndex, 'horizontalCrs'>, projectCrs: string | null | undefined): boolean {
  const a = epsgCode(index.horizontalCrs), b = epsgCode(projectCrs);
  return !a || !b || a === b;
}

// ------------------------------------------------------------------ formatering (norsk)

const nb = (n: number, d = 1) => n.toLocaleString('nb-NO', { minimumFractionDigits: d, maximumFractionDigits: d });

/** «27.05.2026 11:51:49» i norsk tid. */
export function formatImageTime(t: string | null, withSeconds = true): string {
  if (!t) return 'ukjent tid';
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return 'ukjent tid';
  return d.toLocaleString('nb-NO', { timeZone: 'Europe/Oslo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}) }).replace(',', '');
}

const DIRS = ['N', 'NØ', 'Ø', 'SØ', 'S', 'SV', 'V', 'NV'];
/** Kompassretning: «SØ 158°». */
export function compassText(deg: number | null): string {
  if (deg === null) return 'ukjent';
  const d = ((deg % 360) + 360) % 360;
  return `${DIRS[Math.round(d / 45) % 8]} ${Math.round(d)}°`;
}

/** Kameraets helning: «rett ned (−89,7°)», «skrått ned (−45,0°)», «horisontalt (−0,5°)». */
export function pitchText(p: number | null): string {
  if (p === null) return 'ukjent';
  const v = nb(p).replace('-', '−');
  const kind = p <= -75 ? 'rett ned' : p <= -15 ? 'skrått ned' : p < 15 ? 'horisontalt' : 'oppover';
  return `${kind} (${v}°)`;
}

export function rtkText(flag: number | null): string | null {
  return flag === 50 ? 'RTK fast' : flag === 34 ? 'RTK flytende' : flag === 16 ? 'Enkel GNSS (ikke RTK)' : null;
}

/** «Flytur 029 · 27.05.2026» fra DJI-mappenavnet (DJI_202605271136_029_…), ellers mappenavnet. */
export function missionLabel(mission: string): string {
  const m = /^DJI_(\d{4})(\d{2})(\d{2})\d{4}_(\d{3})/.exec(mission);
  return m ? `Flytur ${m[4]} · ${m[3]}.${m[2]}.${m[1]}` : mission || 'ukjent flytur';
}

export const heightRefLabel = (r: ZReference) => (r === 'NN2000' ? 'NN2000' : r === 'ELLIPSOIDAL' ? 'ellipsoidisk' : 'ukjent referanse');

export function formatMeters(v: number | null, d = 1): string {
  return v === null ? '–' : `${nb(v, d).replace('-', '−')} m`;
}

export function formatBytesShort(n: number | null): string {
  if (!n) return '';
  return n >= 1e6 ? `${nb(n / 1e6)} MB` : `${Math.round(n / 1e3)} kB`;
}

// ------------------------------------------------------------------ navigasjon

/** Bildeliste i lysboksen: hele flylinja (tid) eller et utvalg (f.eks. «bilder av denne masten»). */
export interface ImageSequence { kind: 'flight' | 'object' | 'here'; title: string; ids: string[] }

/** Neste/forrige i lista (stopper i endene – ingen omløp, så man merker at flylinja er slutt). */
export function stepSequence(seq: ImageSequence, currentId: string, dir: number): string | null {
  const i = seq.ids.indexOf(currentId);
  if (i < 0) return seq.ids[0] ?? null;
  const j = i + dir;
  return j >= 0 && j < seq.ids.length ? seq.ids[j] : null;
}

export function sequencePosition(seq: ImageSequence, id: string): { index: number; total: number } {
  return { index: seq.ids.indexOf(id), total: seq.ids.length };
}
